import { useEffect, useRef, useState } from "react";
import { Play, X, Volume2, VolumeX, Sparkles } from "lucide-react";
import { toFullUrl } from "@/components/landing/utils";

const isMobile = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

/**
 * FounderVideo — lazy, sin pantallas negras.
 * - Solo carga el video cuando entra al viewport (poster/skeleton mientras tanto).
 * - Móvil: reproduce inline con controles nativos (sin overlay negro a pantalla completa).
 * - Desktop: modal con object-contain y fondo difuminado.
 */
export default function FounderVideo({ videoUrl, poster, eyebrow = "Mensaje del fundador", duration = null, autoplay = false }) {
  const src = toFullUrl(videoUrl);
  const posterUrl = toFullUrl(poster);
  const wrapRef = useRef(null);
  const inlineRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [inlineMuted, setInlineMuted] = useState(true);
  const [inlineActive, setInlineActive] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !src) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [src]);

  useEffect(() => {
    if (!autoplay || !inView) return;
    const v = inlineRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
  }, [autoplay, inView]);

  const toggleInlineMute = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const v = inlineRef.current;
    if (!v) return;
    const next = !inlineMuted;
    v.muted = next;
    setInlineMuted(next);
    if (!next) v.play().catch(() => {});
  };

  const openPlayer = () => {
    if (isMobile()) {
      const v = inlineRef.current;
      if (!v) return;
      setInlineActive(true);
      setInlineMuted(false);
      v.muted = false;
      v.controls = true;
      v.currentTime = 0;
      v.play().catch(() => {});
      return;
    }
    setOpen(true);
  };

  if (!src) {
    return (
      <div className="relative rounded-2xl overflow-hidden border border-black/8 aspect-video bg-gradient-to-br from-[#1B1B21] via-[#1F1F26] to-[#0A0A0B] group">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-[#FFBF57]/15 border border-[#FFBF57]/45 flex items-center justify-center pulse-glow">
            <Play size={20} className="text-[#FFBF57] ml-1" strokeWidth={1.8} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#FFBF57] font-mono">{eyebrow} · Próximamente</div>
          <div className="text-xs text-[#71717A] max-w-[220px] leading-relaxed">Conoce el proyecto directamente de quienes lo construyen.</div>
        </div>
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
      </div>
    );
  }

  const showOverlay = !inlineActive;

  return (
    <>
      <div
        ref={wrapRef}
        className="relative rounded-2xl overflow-hidden border border-black/10 aspect-video bg-[#18181B] group w-full"
        data-testid="founder-video-wrap"
      >
        {/* Skeleton / poster mientras carga */}
        {posterUrl ? (
          <img src={posterUrl} alt="" loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${ready && (autoplay || inlineActive) ? "opacity-0" : "opacity-100"}`} />
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br from-[#3F3F46] via-[#27272A] to-[#18181B] transition-opacity duration-700 ${ready ? "opacity-0" : "opacity-100"}`}>
            <div className="absolute inset-0 grid-lines opacity-30" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full border border-[#FFBF57]/50 bg-[#FFBF57]/10 flex items-center justify-center pulse-glow">
                <Play size={18} className="text-[#FFBF57] ml-0.5" strokeWidth={1.8} />
              </div>
            </div>
          </div>
        )}

        {inView && (
          <video
            ref={inlineRef}
            src={src}
            poster={posterUrl || undefined}
            muted
            loop={!inlineActive}
            playsInline
            autoPlay={autoplay}
            preload={autoplay ? "auto" : "metadata"}
            onLoadedData={() => setReady(true)}
            onPlaying={() => setReady(true)}
            onEnded={() => setInlineActive(false)}
            className={`absolute inset-0 w-full h-full ${inlineActive ? "object-contain bg-[#0A0A0A]" : "object-cover"} transition-opacity duration-700 ${ready && (autoplay || inlineActive) ? "opacity-100" : "opacity-0"}`}
            data-testid="founder-video-inline"
          />
        )}

        {showOverlay && (
          <button
            type="button"
            onClick={openPlayer}
            className="absolute inset-0 w-full h-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFBF57]"
            data-testid="founder-video-open"
            aria-label="Reproducir mensaje del fundador"
          >
            <div className={`absolute inset-0 pointer-events-none ${autoplay ? "bg-gradient-to-t from-black/70 via-transparent to-black/20" : "bg-gradient-to-t from-black/80 via-black/30 to-black/20"}`} />
            <div className="absolute top-3 left-3 pill pill-gold !text-[9px] !py-1 !px-2.5 z-10"><Sparkles size={10} /> {eyebrow}</div>
            {duration && (
              <div className="absolute top-3 right-3 z-10 text-[10px] font-mono uppercase tracking-widest text-white/90 bg-black/50 px-2 py-1 rounded-full backdrop-blur-md">{duration}</div>
            )}
            {!autoplay && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-[#FFBF57]/20 blur-2xl scale-125 group-hover:scale-150 transition-transform duration-700" />
                  <div className="relative w-16 h-16 md:w-24 md:h-24 rounded-full bg-white/[0.08] backdrop-blur-md border border-white/25 flex items-center justify-center group-hover:bg-[#FFBF57] group-hover:border-[#FFBF57] transition-all duration-500 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                    <Play size={26} strokeWidth={1.5} className="text-white group-hover:text-black ml-1 transition-colors duration-500" />
                  </div>
                </div>
              </div>
            )}
            <div className="absolute bottom-3 left-3 z-10 flex items-center gap-2 text-[10px] md:text-xs font-mono uppercase tracking-widest text-white/85">
              <Play size={12} className="text-[#FFBF57]" /> {autoplay ? "Ver con audio" : "Ver mensaje completo"}
            </div>
          </button>
        )}

        {autoplay && showOverlay && (
          <span
            role="button"
            tabIndex={0}
            onClick={toggleInlineMute}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleInlineMute(e); }}
            className="absolute bottom-3 right-3 z-20 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center backdrop-blur-md hover:bg-[#FFBF57] hover:border-[#FFBF57] transition-colors"
            aria-label={inlineMuted ? "Activar audio" : "Silenciar"}
            data-testid="founder-video-inline-mute"
          >
            {inlineMuted ? <VolumeX size={15} className="text-white" /> : <Volume2 size={15} className="text-black" />}
          </span>
        )}
      </div>

      {open && <FounderVideoModal src={src} poster={posterUrl} onClose={() => setOpen(false)} />}
    </>
  );
}

function FounderVideoModal({ src, poster, onClose }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.play().catch(() => { v.muted = true; setMuted(true); v.play().catch(() => {}); });
    }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const next = !muted;
    videoRef.current.muted = next;
    setMuted(next);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A0A0A]/70 backdrop-blur-xl flex items-center justify-center p-4 md:p-10 fade-up" onClick={onClose} data-testid="founder-video-modal">
      <div className="relative w-full max-w-5xl max-h-[85vh] aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.6)] bg-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <video ref={videoRef} src={src} poster={poster} controls playsInline className="w-full h-full object-contain" />
        <button onClick={toggleMute} className="absolute top-4 right-16 w-11 h-11 rounded-full bg-black/60 border border-white/15 flex items-center justify-center backdrop-blur-md hover:bg-[#FFBF57] hover:border-[#FFBF57] transition-colors group" aria-label={muted ? "Activar audio" : "Silenciar"} data-testid="founder-video-mute">
          {muted ? <VolumeX size={17} className="text-white group-hover:text-black" /> : <Volume2 size={17} className="text-white group-hover:text-black" />}
        </button>
        <button onClick={onClose} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-black/60 border border-white/15 flex items-center justify-center backdrop-blur-md hover:bg-[#FFBF57] hover:border-[#FFBF57] transition-colors group" aria-label="Cerrar" data-testid="founder-video-close">
          <X size={17} className="text-white group-hover:text-black" />
        </button>
      </div>
    </div>
  );
}
